import { Chip } from '@/components/ui/Chip';
import type { CandidateStatus, FlystStage, PaymentStatus } from '@/api/schema';

import {
  FLYST_LABEL,
  FLYST_TONE,
  labelledBy,
  PAYMENT_LABEL,
  PAYMENT_TONE,
  STATUS_LABEL,
  STATUS_TONE,
} from '../labels';
import { HistoryTooltip, type HistoryAnchor } from './HistoryTooltip';

/**
 * The candidate's overall status.
 *
 * A component rather than an inline `<Chip>` at each call site, because Phase 16 puts
 * the status-history hover card around exactly this. It went in here once and the
 * table, the detail page and the boards get it together.
 *
 * `history` is required. Optional, "applied to every status chip" would be a habit;
 * required, it is something the compiler checks. A chip with no candidate behind it
 * (a legend, a key) wants a plain `<Chip>`, which is still there.
 *
 * The dot is here and not on the other two: colour must never be the only signal
 * (plan 9.5), and dotting all three chips in one row is noise rather than clarity.
 */
export function StatusChip({
  status,
  history,
}: {
  status: CandidateStatus;
  history: HistoryAnchor;
}) {
  return (
    <HistoryTooltip
      {...history}
      field="status"
      label="Status history"
      describe={labelledBy(STATUS_LABEL)}
    >
      <Chip tone={STATUS_TONE[status]} dot>
        {STATUS_LABEL[status]}
      </Chip>
    </HistoryTooltip>
  );
}

export function FlystStageChip({
  stage,
  history,
}: {
  stage: FlystStage;
  history: HistoryAnchor;
}) {
  return (
    <HistoryTooltip
      {...history}
      field="flyst_stage"
      label="Flyst screening history"
      describe={labelledBy(FLYST_LABEL)}
    >
      <Chip tone={FLYST_TONE[stage]} size="sm">
        {FLYST_LABEL[stage]}
      </Chip>
    </HistoryTooltip>
  );
}

export function PaymentChip({
  status,
  history,
}: {
  status: PaymentStatus;
  history: HistoryAnchor;
}) {
  return (
    <HistoryTooltip
      {...history}
      field="payment_status"
      label="Payment history"
      describe={labelledBy(PAYMENT_LABEL)}
    >
      <Chip tone={PAYMENT_TONE[status]} size="sm">
        {PAYMENT_LABEL[status]}
      </Chip>
    </HistoryTooltip>
  );
}
